/**
 * useShiftDecisions (BON-243) — confirm/decline mutations for the RD shift
 * board, with the board-invalidation and error handling both share.
 *
 * Its own module so RegionalShiftBoard stays under the lint size cap.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { confirmShiftPickup, declineShiftPickup } from '../../../api/openShifts';

export interface ShiftDecisions {
  error: string | null;
  /** Set when the decision committed but some emails didn't send. The staffing
   *  is durable either way, so this reads as a warning, never as a failure. */
  warning: string | null;
  busy: boolean;
  confirm: (shiftId: string, userId: string) => void;
  decline: (shiftId: string, userId: string) => void;
}

export function useShiftDecisions(regionSlug: string): ShiftDecisions {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const boardKey = ['dashboards.regional', regionSlug, 'shifts'];
  const refresh = () => qc.invalidateQueries({ queryKey: boardKey });

  const opts = {
    onMutate: () => {
      setError(null);
      setWarning(null);
    },
    onSuccess: (result: { notified: { failed: number } }) => {
      if (result.notified.failed > 0) {
        setWarning(
          `Decision saved, but ${result.notified.failed} notification email${
            result.notified.failed === 1 ? '' : 's'
          } could not be sent.`,
        );
      }
      refresh();
    },
    onError: (err: Error) => {
      setError(err.message);
      // A 409 here means the board is stale — someone else already confirmed,
      // or the requester withdrew. Refetch so the dead row goes away.
      refresh();
    },
  };

  const confirmMut = useMutation({ mutationFn: confirmShiftPickup, ...opts });
  const declineMut = useMutation({ mutationFn: declineShiftPickup, ...opts });

  return {
    error,
    warning,
    busy: confirmMut.isPending || declineMut.isPending,
    confirm: (shiftId, userId) => confirmMut.mutate({ shiftId, userId }),
    decline: (shiftId, userId) => declineMut.mutate({ shiftId, userId }),
  };
}
